/* eslint-disable jira/filename-case */
import { merge } from 'icepick';
import {
	getTraceId,
	type FetchInput,
	type FetchResponse,
} from '@atlassian/expansion-services/services';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { getReroutableURL } from '@atlassian/jira-fetch/src/utils/get-reroutable-url.tsx';
import { applyObservabilityHeaders } from '@atlassian/jira-fetch/src/utils/observability-headers.tsx';
import { defaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';

/**
 * Fetch JSON from the given URL, returning a FetchResponse with data and optional traceId.
 * Replicates important semantics from jira-fetch's fetchJson:
 * - Reroutable URL handling for local dev
 * - Observability headers (capability, tracing)
 * - Default options (credentials, content-type, accept headers)
 * - HTTP error handling with status and traceId in FetchError
 * - TraceId extraction from response headers on success
 *
 * @param input - FetchInput containing url and RequestInit options
 * @returns Promise<FetchResponse> with data and optional traceId
 * @throws FetchError on HTTP error status
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const jiraFetchJson = async (input: FetchInput): Promise<FetchResponse<any>> => {
	const { url, ...options } = input;

	// Apply all jira-fetch semantics: reroutable URL, observability headers, default options
	const reroutableUrl = getReroutableURL(url);
	const mergedOptions = merge(defaultOptions, options);
	const finalOptions = applyObservabilityHeaders(reroutableUrl, mergedOptions);

	const response = await fetch(reroutableUrl, finalOptions);

	if (!response.ok) {
		const traceId = getTraceId(response);
		if (traceId !== undefined) {
			throw new FetchError(
				response.status,
				`Fetch call failed with status code: ${response.status}`,
				traceId,
				response,
			);
		} else {
			throw new FetchError(response.status, undefined, undefined, response);
		}
	}

	// Parse JSON and extract traceId from response headers
	const data: unknown = await response.json();
	const traceId = getTraceId(response);

	return traceId === undefined ? { data } : { data, traceId };
};
